import { makeApi, Zodios, type ZodiosOptions } from '@zodios/core';
import { z } from 'zod';

const UserRolesRequest = z.object({ role: z.enum(['admin', 'user']) }).passthrough();
const x_request_id = z.union([z.string(), z.null()]).optional();
const ValidationError = z
  .object({ loc: z.array(z.union([z.string(), z.number()])), msg: z.string(), type: z.string() })
  .passthrough();
const HTTPValidationError = z
  .object({ detail: z.array(ValidationError) })
  .partial()
  .passthrough();
const Invitation = z
  .object({
    id: z.string(),
    inviter: z.string(),
    invitee: z.string(),
    created_at: z.string(),
    expires_at: z.string(),
    role: z.enum(['admin', 'user', 'unauthorized']),
    client_id: z.string(),
  })
  .passthrough();
const InvitationListResponse = z.object({ items: z.array(Invitation) }).passthrough();
const InviteUserRequest = z
  .object({ email: z.string().email(), role: z.enum(['admin', 'user']) })
  .passthrough();
const ProjectKeys = z
  .object({ id: z.union([z.string(), z.null()]).optional(), tenant_id: z.string() })
  .passthrough();
const UsageLimits = z
  .object({
    limit_monthly_bill: z.union([z.number(), z.null()]),
    alert_monthly_bill: z.union([z.number(), z.null()]),
  })
  .partial()
  .passthrough();
const ProjectSpec = z
  .object({
    name: z.string().min(1).max(63),
    status: z.enum(['active', 'archived']),
    usage_limits: z.union([UsageLimits, z.null()]).optional(),
    allow_all_users: z.union([z.boolean(), z.null()]).optional().default(false),
  })
  .passthrough();
const Meta = z
  .object({
    created_by: z.union([z.string(), z.null()]),
    created_at: z.unknown(),
    updated_by: z.union([z.string(), z.null()]),
    updated_at: z.unknown(),
  })
  .partial()
  .passthrough();
const ObjectBase_ProjectKeys_ProjectSpec_ = z
  .object({ keys: ProjectKeys, spec: ProjectSpec, meta: z.union([Meta, z.null()]).optional() })
  .passthrough();
const ProjectListResponse = z
  .object({ items: z.array(ObjectBase_ProjectKeys_ProjectSpec_) })
  .passthrough();
const ProjectUpdateRequest = z
  .object({ name: z.union([z.string(), z.null()]), usage_limits: z.union([UsageLimits, z.null()]) })
  .partial()
  .passthrough();
const TenantUpdateRequest = z
  .object({
    name: z.union([z.string(), z.null()]),
    usage_limits: z.union([UsageLimits, z.null()]),
    enable_mfa: z.union([z.boolean(), z.null()]),
    encrypt_reasoning: z.union([z.boolean(), z.null()]),
  })
  .partial()
  .passthrough();
const TokenConsumed = z
  .object({ input_tokens: z.number().int(), output_tokens: z.number().int() })
  .passthrough();
const TokenHistory = z
  .object({
    start_time: z.string().datetime({ offset: true }),
    end_time: z.string().datetime({ offset: true }),
    bill_spent: z.number(),
    usage: z.record(z.record(TokenConsumed)),
  })
  .passthrough();
const TokenHistoryResponse = z
  .object({ items: z.array(TokenHistory), currency: z.enum(['JPY', 'USD']) })
  .passthrough();
const CreditGrant = z
  .object({
    name: z.union([z.string(), z.null()]),
    currency: z.enum(['JPY', 'USD']),
    amount: z.number().int(),
    effective_at: z.union([z.string(), z.null()]),
    expires_at: z.union([z.string(), z.null()]),
  })
  .passthrough();
const ListResponse_CreditGrant_ = z.object({ items: z.array(CreditGrant) }).passthrough();
const BillingPortalUrl = z.object({ url: z.string() }).passthrough();
const User = z
  .object({
    user_id: z.string(),
    email: z.string(),
    picture: z.string(),
    name: z.string(),
    role: z.enum(['admin', 'user', 'unauthorized']),
    roles: z.array(z.string()).optional().default([]),
  })
  .passthrough();
const UserListResponse = z.object({ items: z.array(User) }).passthrough();
const ApiKeyKeys = z
  .object({ id: z.union([z.string(), z.null()]).optional(), tenant_id: z.string() })
  .passthrough();
const ApiKeySpec = z
  .object({
    name: z.string(),
    email: z.string(),
    masked_secret: z.string(),
    status: z.enum(['active', 'archived']),
    user_id: z.union([z.string(), z.null()]).optional(),
    project_id: z.union([z.string(), z.null()]).optional(),
  })
  .passthrough();
const ObjectBase_ApiKeyKeys_ApiKeySpec_ = z
  .object({ keys: ApiKeyKeys, spec: ApiKeySpec, meta: z.union([Meta, z.null()]).optional() })
  .passthrough();
const ApiKeyResponse = z.object({ api_key: z.string() }).passthrough();
const AllowedProject = z
  .object({
    name: z.string().min(1).max(63),
    status: z.enum(['active', 'archived']),
    usage_limits: z.union([UsageLimits, z.null()]).optional(),
    allow_all_users: z.union([z.boolean(), z.null()]).optional().default(false),
    id: z.string(),
    role: z.enum(['owner', 'member']),
  })
  .passthrough();
const AllowedProjectListResponse = z.object({ items: z.array(AllowedProject) }).passthrough();
const ProjectMember = z
  .object({
    user_id: z.string(),
    project_role: z.enum(['owner', 'member']),
    email: z.string(),
    name: z.string(),
    picture: z.string(),
  })
  .passthrough();
const ProjectMembersResponse = z.object({ items: z.array(ProjectMember) }).passthrough();
const AddProjectMemberRequest = z
  .object({ user_id: z.string().regex(/^[A-Za-z0-9|-]{3,63}$/), role: z.enum(['owner', 'member']) })
  .passthrough();
const UpdateProjectRoleRequest = z.object({ role: z.enum(['owner', 'member']) }).passthrough();
const ApiKeyListResponse = z
  .object({ items: z.array(ObjectBase_ApiKeyKeys_ApiKeySpec_) })
  .passthrough();
const CreateProjectApiKeyRequest = z.object({ name: z.string().min(1).max(63) }).passthrough();
const UpdateProjectApiKeyRequest = z.object({ name: z.string().min(1).max(63) }).passthrough();
const TenantKeys = z
  .object({ id: z.union([z.string(), z.null()]) })
  .partial()
  .passthrough();
const TenantQuotas = z
  .object({
    monthly_bill: z.number().int(),
    projects: z.number().int(),
    users: z.number().int(),
    api_keys: z.number().int(),
  })
  .passthrough();
const BillingSettings = z.object({ currency: z.enum(['JPY', 'USD']) }).passthrough();
const DiscountScheduleEntry = z
  .object({
    effective_from: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/),
    rate: z.number().gte(0).lt(1),
  })
  .passthrough();
const TenantSpec = z
  .object({
    name: z.string().min(1).max(63),
    status: z.enum(['active', 'delete_scheduled', 'inactive', 'deleted']),
    enable_mfa: z.boolean().optional().default(false),
    customer_id: z.union([z.string(), z.null()]).optional(),
    custom_tenant_type: z.union([z.enum(['trial', 'invoice', 'personal']), z.null()]).optional(),
    usage_limits: z.union([UsageLimits, z.null()]).optional(),
    quotas: TenantQuotas,
    billing_settings: BillingSettings,
    discount_schedule: z.array(DiscountScheduleEntry).optional().default([]),
    subscription_id: z.union([z.string(), z.null()]).optional(),
    downgrade_requested: z.boolean().optional().default(false),
    downgrade_at: z.union([z.string(), z.null()]).optional(),
    owner_email: z.union([z.string(), z.null()]).optional(),
    api_keys_valid_until: z.union([z.string(), z.null()]).optional(),
  })
  .passthrough();
const TenantMetadata = z
  .object({
    contract_plan: z.enum(['free', 'standard', 'provider']),
    encrypt_reasoning: z.boolean(),
  })
  .passthrough();
const TenantWithMetadata = z
  .object({
    keys: TenantKeys,
    spec: TenantSpec,
    meta: z.union([Meta, z.null()]).optional(),
    tenant_metadata: TenantMetadata,
  })
  .passthrough();
const WebSearchUsage = z
  .object({
    daily_count: z.number().int(),
    monthly_count: z.number().int(),
    daily_limit: z.union([z.number(), z.null()]),
    monthly_limit: z.union([z.number(), z.null()]),
  })
  .passthrough();
const SignUpUrl = z.object({ url: z.string() }).passthrough();
const LoginRouteRequest = z.object({ email: z.string() }).passthrough();
const LoginRouteResponse = z.object({ is_paid_tenant: z.boolean() }).passthrough();
const EnsureTenantResponse = z.object({ created: z.boolean() }).passthrough();
const UpgradeCandidate = z.object({ org_id: z.string(), name: z.string() }).passthrough();
const UpgradeCandidatesResponse = z
  .object({ candidates: z.array(UpgradeCandidate), api_key_grace_days: z.number().int() })
  .passthrough();
const UpgradeTargetOrg = z
  .object({ org_id: z.string().regex(/^org_[A-Za-z0-9]{16}$/) })
  .passthrough();
const UpgradeRequest = z.object({ target: z.union([z.string(), UpgradeTargetOrg]) }).passthrough();
const GetPromotionRequest = z.object({ kind: z.string(), code: z.string() }).passthrough();

export const schemas = {
  UserRolesRequest,
  x_request_id,
  ValidationError,
  HTTPValidationError,
  Invitation,
  InvitationListResponse,
  InviteUserRequest,
  ProjectKeys,
  UsageLimits,
  ProjectSpec,
  Meta,
  ObjectBase_ProjectKeys_ProjectSpec_,
  ProjectListResponse,
  ProjectUpdateRequest,
  TenantUpdateRequest,
  TokenConsumed,
  TokenHistory,
  TokenHistoryResponse,
  CreditGrant,
  ListResponse_CreditGrant_,
  BillingPortalUrl,
  User,
  UserListResponse,
  ApiKeyKeys,
  ApiKeySpec,
  ObjectBase_ApiKeyKeys_ApiKeySpec_,
  ApiKeyResponse,
  AllowedProject,
  AllowedProjectListResponse,
  ProjectMember,
  ProjectMembersResponse,
  AddProjectMemberRequest,
  UpdateProjectRoleRequest,
  ApiKeyListResponse,
  CreateProjectApiKeyRequest,
  UpdateProjectApiKeyRequest,
  TenantKeys,
  TenantQuotas,
  BillingSettings,
  DiscountScheduleEntry,
  TenantSpec,
  TenantMetadata,
  TenantWithMetadata,
  WebSearchUsage,
  SignUpUrl,
  LoginRouteRequest,
  LoginRouteResponse,
  EnsureTenantResponse,
  UpgradeCandidate,
  UpgradeCandidatesResponse,
  UpgradeTargetOrg,
  UpgradeRequest,
  GetPromotionRequest,
};

const endpoints = makeApi([
  {
    method: 'post',
    path: '/admin/v1/billing/portal-session',
    alias: 'admin_v1_create_billing_portal_session',
    requestFormat: 'json',
    parameters: [
      {
        name: 'x-request-id',
        type: 'Header',
        schema: x_request_id,
      },
      {
        name: 'authorization',
        type: 'Header',
        schema: x_request_id,
      },
    ],
    response: z.object({ url: z.string() }).passthrough(),
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/admin/v1/invitations',
    alias: 'admin_v1_get_invitations',
    requestFormat: 'json',
    parameters: [
      {
        name: 'x-request-id',
        type: 'Header',
        schema: x_request_id,
      },
      {
        name: 'authorization',
        type: 'Header',
        schema: x_request_id,
      },
    ],
    response: InvitationListResponse,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/admin/v1/invitations',
    alias: 'admin_v1_invite_user',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: InviteUserRequest,
      },
      {
        name: 'x-request-id',
        type: 'Header',
        schema: x_request_id,
      },
      {
        name: 'authorization',
        type: 'Header',
        schema: x_request_id,
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'delete',
    path: '/admin/v1/invitations/:invitation_id',
    alias: 'admin_v1_delete_invitation',
    requestFormat: 'json',
    parameters: [
      {
        name: 'invitation_id',
        type: 'Path',
        schema: z.string(),
      },
      {
        name: 'x-request-id',
        type: 'Header',
        schema: x_request_id,
      },
      {
        name: 'authorization',
        type: 'Header',
        schema: x_request_id,
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/admin/v1/projects',
    alias: 'admin_v1_get_all_projects',
    requestFormat: 'json',
    parameters: [
      {
        name: 'status',
        type: 'Query',
        schema: z.enum(['active', 'archived']).optional().default('active'),
      },
      {
        name: 'x-request-id',
        type: 'Header',
        schema: x_request_id,
      },
      {
        name: 'authorization',
        type: 'Header',
        schema: x_request_id,
      },
    ],
    response: ProjectListResponse,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/admin/v1/projects',
    alias: 'admin_v1_create_project',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ProjectSpec,
      },
      {
        name: 'x-request-id',
        type: 'Header',
        schema: x_request_id,
      },
      {
        name: 'authorization',
        type: 'Header',
        schema: x_request_id,
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'patch',
    path: '/admin/v1/projects/:project_id',
    alias: 'admin_v1_update_project',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ProjectUpdateRequest,
      },
      {
        name: 'project_id',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'x-request-id',
        type: 'Header',
        schema: x_request_id,
      },
      {
        name: 'authorization',
        type: 'Header',
        schema: x_request_id,
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'delete',
    path: '/admin/v1/projects/:project_id',
    alias: 'admin_v1_archive_project',
    requestFormat: 'json',
    parameters: [
      {
        name: 'project_id',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'x-request-id',
        type: 'Header',
        schema: x_request_id,
      },
      {
        name: 'authorization',
        type: 'Header',
        schema: x_request_id,
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'patch',
    path: '/admin/v1/tenant',
    alias: 'admin_v1_update_tenant',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: TenantUpdateRequest,
      },
      {
        name: 'x-request-id',
        type: 'Header',
        schema: x_request_id,
      },
      {
        name: 'authorization',
        type: 'Header',
        schema: x_request_id,
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/admin/v1/tenant/credits',
    alias: 'admin_v1_get_tenant_credits',
    requestFormat: 'json',
    parameters: [
      {
        name: 'x-request-id',
        type: 'Header',
        schema: x_request_id,
      },
      {
        name: 'authorization',
        type: 'Header',
        schema: x_request_id,
      },
    ],
    response: ListResponse_CreditGrant_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/admin/v1/tenant/token-history',
    alias: 'admin_v1_get_tenant_token_history',
    requestFormat: 'json',
    parameters: [
      {
        name: 'year',
        type: 'Query',
        schema: z.number().int().gte(2024).lte(2030),
      },
      {
        name: 'month',
        type: 'Query',
        schema: z.number().int().gte(1).lte(12),
      },
      {
        name: 'order',
        type: 'Query',
        schema: z.enum(['ASC', 'DESC']).optional().default('ASC'),
      },
      {
        name: 'x-request-id',
        type: 'Header',
        schema: x_request_id,
      },
      {
        name: 'authorization',
        type: 'Header',
        schema: x_request_id,
      },
    ],
    response: TokenHistoryResponse,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'delete',
    path: '/admin/v1/users/:user_id',
    alias: 'admin_v1_remove_user',
    requestFormat: 'json',
    parameters: [
      {
        name: 'user_id',
        type: 'Path',
        schema: z.string(),
      },
      {
        name: 'x-request-id',
        type: 'Header',
        schema: x_request_id,
      },
      {
        name: 'authorization',
        type: 'Header',
        schema: x_request_id,
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/admin/v1/users/:user_id/roles',
    alias: 'admin_v1_update_user_role',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UserRolesRequest,
      },
      {
        name: 'user_id',
        type: 'Path',
        schema: z.string(),
      },
      {
        name: 'x-request-id',
        type: 'Header',
        schema: x_request_id,
      },
      {
        name: 'authorization',
        type: 'Header',
        schema: x_request_id,
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/settings/v1/projects',
    alias: 'settings_v1_get_projects',
    requestFormat: 'json',
    parameters: [
      {
        name: 'x-request-id',
        type: 'Header',
        schema: x_request_id,
      },
      {
        name: 'authorization',
        type: 'Header',
        schema: x_request_id,
      },
    ],
    response: AllowedProjectListResponse,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/settings/v1/projects/:project_id',
    alias: 'settings_v1_get_project',
    requestFormat: 'json',
    parameters: [
      {
        name: 'project_id',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'x-request-id',
        type: 'Header',
        schema: x_request_id,
      },
      {
        name: 'authorization',
        type: 'Header',
        schema: x_request_id,
      },
    ],
    response: AllowedProject,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/settings/v1/projects/:project_id/api-keys',
    alias: 'settings_v1_get_project_api_keys',
    requestFormat: 'json',
    parameters: [
      {
        name: 'project_id',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'x-request-id',
        type: 'Header',
        schema: x_request_id,
      },
      {
        name: 'authorization',
        type: 'Header',
        schema: x_request_id,
      },
    ],
    response: ApiKeyListResponse,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/settings/v1/projects/:project_id/api-keys',
    alias: 'settings_v1_create_project_api_key',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({ name: z.string().min(1).max(63) }).passthrough(),
      },
      {
        name: 'project_id',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'x-request-id',
        type: 'Header',
        schema: x_request_id,
      },
      {
        name: 'authorization',
        type: 'Header',
        schema: x_request_id,
      },
    ],
    response: z.object({ api_key: z.string() }).passthrough(),
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'patch',
    path: '/settings/v1/projects/:project_id/api-keys/:api_key_id',
    alias: 'settings_v1_update_project_api_key',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({ name: z.string().min(1).max(63) }).passthrough(),
      },
      {
        name: 'project_id',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'api_key_id',
        type: 'Path',
        schema: z.string().max(63),
      },
      {
        name: 'x-request-id',
        type: 'Header',
        schema: x_request_id,
      },
      {
        name: 'authorization',
        type: 'Header',
        schema: x_request_id,
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'delete',
    path: '/settings/v1/projects/:project_id/api-keys/:api_key_id',
    alias: 'settings_v1_delete_project_api_key',
    requestFormat: 'json',
    parameters: [
      {
        name: 'project_id',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'api_key_id',
        type: 'Path',
        schema: z.string().max(63),
      },
      {
        name: 'x-request-id',
        type: 'Header',
        schema: x_request_id,
      },
      {
        name: 'authorization',
        type: 'Header',
        schema: x_request_id,
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/settings/v1/projects/:project_id/api-keys/:api_key_id/generate',
    alias: 'settings_v1_generate_project_api_key',
    requestFormat: 'json',
    parameters: [
      {
        name: 'project_id',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'api_key_id',
        type: 'Path',
        schema: z.string().max(63),
      },
      {
        name: 'x-request-id',
        type: 'Header',
        schema: x_request_id,
      },
      {
        name: 'authorization',
        type: 'Header',
        schema: x_request_id,
      },
    ],
    response: z.object({ api_key: z.string() }).passthrough(),
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/settings/v1/projects/:project_id/members',
    alias: 'settings_v1_get_project_members',
    requestFormat: 'json',
    parameters: [
      {
        name: 'project_id',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'x-request-id',
        type: 'Header',
        schema: x_request_id,
      },
      {
        name: 'authorization',
        type: 'Header',
        schema: x_request_id,
      },
    ],
    response: ProjectMembersResponse,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/settings/v1/projects/:project_id/members',
    alias: 'settings_v1_add_project_member',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AddProjectMemberRequest,
      },
      {
        name: 'project_id',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'x-request-id',
        type: 'Header',
        schema: x_request_id,
      },
      {
        name: 'authorization',
        type: 'Header',
        schema: x_request_id,
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'delete',
    path: '/settings/v1/projects/:project_id/members/:user_id',
    alias: 'settings_v1_remove_project_member',
    requestFormat: 'json',
    parameters: [
      {
        name: 'project_id',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'user_id',
        type: 'Path',
        schema: z.string(),
      },
      {
        name: 'x-request-id',
        type: 'Header',
        schema: x_request_id,
      },
      {
        name: 'authorization',
        type: 'Header',
        schema: x_request_id,
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/settings/v1/projects/:project_id/members/:user_id/roles',
    alias: 'settings_v1_update_project_member_role',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UpdateProjectRoleRequest,
      },
      {
        name: 'project_id',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'user_id',
        type: 'Path',
        schema: z.string(),
      },
      {
        name: 'x-request-id',
        type: 'Header',
        schema: x_request_id,
      },
      {
        name: 'authorization',
        type: 'Header',
        schema: x_request_id,
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/settings/v1/projects/:project_id/token-history',
    alias: 'settings_v1_get_project_token_history',
    requestFormat: 'json',
    parameters: [
      {
        name: 'project_id',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'year',
        type: 'Query',
        schema: z.number().int().gte(2024).lte(2030),
      },
      {
        name: 'month',
        type: 'Query',
        schema: z.number().int().gte(1).lte(12),
      },
      {
        name: 'order',
        type: 'Query',
        schema: z.enum(['ASC', 'DESC']).optional().default('ASC'),
      },
      {
        name: 'x-request-id',
        type: 'Header',
        schema: x_request_id,
      },
      {
        name: 'authorization',
        type: 'Header',
        schema: x_request_id,
      },
    ],
    response: TokenHistoryResponse,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/settings/v1/tenant',
    alias: 'settings_v1_get_tenant',
    requestFormat: 'json',
    parameters: [
      {
        name: 'x-request-id',
        type: 'Header',
        schema: x_request_id,
      },
      {
        name: 'authorization',
        type: 'Header',
        schema: x_request_id,
      },
    ],
    response: TenantWithMetadata,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/settings/v1/tenant/downgrade',
    alias: 'settings_v1_downgrade_tenant_to_free',
    requestFormat: 'json',
    parameters: [
      {
        name: 'x-request-id',
        type: 'Header',
        schema: x_request_id,
      },
      {
        name: 'authorization',
        type: 'Header',
        schema: x_request_id,
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/settings/v1/tenant/downgrade/cancel',
    alias: 'settings_v1_cancel_tenant_downgrade',
    requestFormat: 'json',
    parameters: [
      {
        name: 'x-request-id',
        type: 'Header',
        schema: x_request_id,
      },
      {
        name: 'authorization',
        type: 'Header',
        schema: x_request_id,
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/settings/v1/tenant/web-search-usage',
    alias: 'settings_v1_get_tenant_web_search_usage',
    requestFormat: 'json',
    parameters: [
      {
        name: 'x-request-id',
        type: 'Header',
        schema: x_request_id,
      },
      {
        name: 'authorization',
        type: 'Header',
        schema: x_request_id,
      },
    ],
    response: WebSearchUsage,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/settings/v1/user',
    alias: 'settings_v1_get_user_profile',
    requestFormat: 'json',
    parameters: [
      {
        name: 'x-request-id',
        type: 'Header',
        schema: x_request_id,
      },
      {
        name: 'authorization',
        type: 'Header',
        schema: x_request_id,
      },
    ],
    response: User,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/settings/v1/user/api-key',
    alias: 'settings_v1_get_personal_api_key',
    requestFormat: 'json',
    parameters: [
      {
        name: 'x-request-id',
        type: 'Header',
        schema: x_request_id,
      },
      {
        name: 'authorization',
        type: 'Header',
        schema: x_request_id,
      },
    ],
    response: ObjectBase_ApiKeyKeys_ApiKeySpec_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/settings/v1/user/api-key',
    alias: 'settings_v1_generate_personal_api_key',
    requestFormat: 'json',
    parameters: [
      {
        name: 'x-request-id',
        type: 'Header',
        schema: x_request_id,
      },
      {
        name: 'authorization',
        type: 'Header',
        schema: x_request_id,
      },
    ],
    response: z.object({ api_key: z.string() }).passthrough(),
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/settings/v1/users',
    alias: 'settings_v1_get_users',
    requestFormat: 'json',
    parameters: [
      {
        name: 'x-request-id',
        type: 'Header',
        schema: x_request_id,
      },
      {
        name: 'authorization',
        type: 'Header',
        schema: x_request_id,
      },
    ],
    response: UserListResponse,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/signup/v1/callback',
    alias: 'signup_v1_checkout_callback',
    requestFormat: 'json',
    parameters: [
      {
        name: 'success',
        type: 'Query',
        schema: z.boolean(),
      },
      {
        name: 'session_id',
        type: 'Query',
        schema: x_request_id,
      },
      {
        name: 'checkout_id',
        type: 'Query',
        schema: x_request_id,
      },
      {
        name: 'x-request-id',
        type: 'Header',
        schema: x_request_id,
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/signup/v1/callback',
    alias: 'signup_v1_post_checkout_callback',
    requestFormat: 'json',
    parameters: [
      {
        name: 'success',
        type: 'Query',
        schema: z.boolean(),
      },
      {
        name: 'session_id',
        type: 'Query',
        schema: x_request_id,
      },
      {
        name: 'checkout_id',
        type: 'Query',
        schema: x_request_id,
      },
      {
        name: 'x-request-id',
        type: 'Header',
        schema: x_request_id,
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/signup/v1/check-session',
    alias: 'signup_v1_get_session',
    requestFormat: 'json',
    parameters: [
      {
        name: 'user_id',
        type: 'Query',
        schema: z.string().regex(/^[A-Za-z0-9|-]{3,63}$/),
      },
      {
        name: 'x-request-id',
        type: 'Header',
        schema: x_request_id,
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/signup/v1/checkout',
    alias: 'signup_v1_checkout',
    requestFormat: 'json',
    parameters: [
      {
        name: 'session_id',
        type: 'Query',
        schema: z.string(),
      },
      {
        name: 'user_id',
        type: 'Query',
        schema: z.string().regex(/^[A-Za-z0-9|-]{3,63}$/),
      },
      {
        name: 'x-request-id',
        type: 'Header',
        schema: x_request_id,
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 302,
        description: `Successful Response`,
        schema: z.unknown(),
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/signup/v1/ensure-tenant',
    alias: 'signup_v1_ensure_tenant',
    description: `無料プランのユーザの個人テナントを、無ければ作成する`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'x-request-id',
        type: 'Header',
        schema: x_request_id,
      },
      {
        name: 'authorization',
        type: 'Header',
        schema: x_request_id,
      },
    ],
    response: z.object({ created: z.boolean() }).passthrough(),
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/signup/v1/login-route',
    alias: 'signup_v1_login_route',
    description: `ログイン前に、email が有料テナント（Auth0 組織）のユーザかを返す。email はログに残さない`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({ email: z.string() }).passthrough(),
      },
      {
        name: 'x-request-id',
        type: 'Header',
        schema: x_request_id,
      },
    ],
    response: z.object({ is_paid_tenant: z.boolean() }).passthrough(),
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/signup/v1/promotion',
    alias: 'signup_v1_create_promotion',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetPromotionRequest,
      },
      {
        name: 'x-request-id',
        type: 'Header',
        schema: x_request_id,
      },
      {
        name: 'authorization',
        type: 'Header',
        schema: x_request_id,
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/signup/v1/start-session',
    alias: 'signup_v1_start_session',
    requestFormat: 'json',
    parameters: [
      {
        name: 'org_id',
        type: 'Query',
        schema: x_request_id,
      },
      {
        name: 'x-request-id',
        type: 'Header',
        schema: x_request_id,
      },
      {
        name: 'authorization',
        type: 'Header',
        schema: x_request_id,
      },
    ],
    response: z.object({ url: z.string() }).passthrough(),
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/signup/v1/upgrade',
    alias: 'signup_v1_upgrade',
    description: `無料プランのユーザを、新しい組織の作成か inactive の組織の再開で有料化する

返す URL はブラウザが GET で遷移する先。`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UpgradeRequest,
      },
      {
        name: 'x-request-id',
        type: 'Header',
        schema: x_request_id,
      },
      {
        name: 'authorization',
        type: 'Header',
        schema: x_request_id,
      },
    ],
    response: z.object({ url: z.string() }).passthrough(),
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/signup/v1/upgrade-candidates',
    alias: 'signup_v1_get_upgrade_candidates',
    description: `無料プランのユーザが再開して有料化できる組織（管理者だった inactive の組織）を返す`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'x-request-id',
        type: 'Header',
        schema: x_request_id,
      },
      {
        name: 'authorization',
        type: 'Header',
        schema: x_request_id,
      },
    ],
    response: UpgradeCandidatesResponse,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
]);

export const api = new Zodios(endpoints);

export function createApiClient(baseUrl: string, options?: ZodiosOptions) {
  return new Zodios(baseUrl, endpoints, options);
}
